import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Clock, CheckCircle, Truck, XCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${id}`, { withCredentials: true });
        setOrder(data);
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const getStatusStep = (status) => {
    switch (status) {
      case 'pending_payment': return 0;
      case 'processing': return 1;
      case 'shipped': return 2;
      case 'delivered': return 3;
      default: return 0;
    }
  };

  const steps = [
    { label: 'Order Placed', icon: Package },
    { label: 'Processing', icon: Clock },
    { label: 'Shipped', icon: Truck },
    { label: 'Delivered', icon: CheckCircle }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="skeleton h-8 w-1/3 mb-8" />
          <div className="glass-card p-6">
            <div className="skeleton h-6 w-1/4 mb-4" />
            <div className="skeleton h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#A1A7B5] text-lg mb-4">Order not found</p>
          <Link to="/orders">
            <Button className="btn-primary">Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentStep = getStatusStep(order.status);

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="order-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/orders" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Orders</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Order #{order.id.slice(0, 8)}
            </h1>
            <p className="text-[#A1A7B5] text-sm">
              Placed on {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
          <span className="text-2xl font-bold text-[#00F0FF]" data-testid="order-total">
            ${order.total.toFixed(2)}
          </span>
        </div>

        {/* Order Status Tracker */}
        {order.status !== 'cancelled' && (
          <div className="glass-card p-6 mb-8" data-testid="order-tracker">
            <h2 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Order Status
            </h2>
            <div className="relative">
              <div className="flex justify-between">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <div key={index} className="flex flex-col items-center relative z-10">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        isCompleted 
                          ? 'bg-[#00F0FF] text-black' 
                          : 'bg-[#1E2532] text-[#A1A7B5]'
                      } ${isCurrent ? 'animate-pulse-glow' : ''}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className={`text-sm mt-2 text-center ${isCompleted ? 'text-white' : 'text-[#A1A7B5]'}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Progress line */}
              <div className="absolute top-6 left-0 right-0 h-0.5 bg-[#1E2532]">
                <div 
                  className="h-full bg-[#00F0FF] transition-all duration-500"
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {order.status === 'cancelled' && (
          <div className="glass-card p-6 mb-8 border-red-400/50">
            <div className="flex items-center space-x-3">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <h2 className="text-lg font-semibold text-red-400">Order Cancelled</h2>
                <p className="text-[#A1A7B5]">This order has been cancelled.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Items */}
          <div className="lg:col-span-2 glass-card p-6">
            <h2 className="text-lg font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Order Items
            </h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex space-x-4 p-4 bg-[#111621] rounded-lg" data-testid={`order-item-${index}`}>
                  <div className="w-16 h-16 bg-[#1E2532] rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-[#A1A7B5]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium">{item.name}</h3>
                    <p className="text-[#A1A7B5] text-sm">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">${item.subtotal.toFixed(2)}</p>
                    <p className="text-[#A1A7B5] text-sm">${item.price.toFixed(2)} each</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1 space-y-6">
            {order.shipping_address && (
              <div className="glass-card p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <MapPin className="w-5 h-5 text-[#00F0FF]" />
                  <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Shipping Address
                  </h2>
                </div>
                <div className="text-[#A1A7B5] text-sm space-y-1">
                  <p className="text-white font-medium">
                    {order.shipping_address.firstName} {order.shipping_address.lastName}
                  </p>
                  <p>{order.shipping_address.address}</p>
                  <p>
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                  </p>
                  <p>{order.shipping_address.country}</p>
                </div>
              </div>
            )}

            <div className="glass-card p-6">
              <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Order Summary
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-[#A1A7B5]">
                  <span>Subtotal</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#A1A7B5]">
                  <span>Shipping</span>
                  <span className="text-[#00F0FF]">FREE</span>
                </div>
                <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-[#1E2532]">
                  <span>Total</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
